import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

interface AuthState {
  token: string | null
  host: string | null
  shop: string | null
  isLoading: boolean
  isHydrated: boolean
  authFailure: {
    installUrl: string | null
    adminHomeUrl: string | null
    reauthStatus: string | null
    /** Gọi khi user đóng modal xin quyền (Cancel / X) — không persist. */
    onDismiss?: () => void
  } | null
  setToken: (token: string) => void
  setHostAndShop: (host: string, shop: string) => void
  clearToken: () => void
  setIsLoading: (value: boolean) => void
  setHydrated: () => void
  setAuthFailure: (payload: AuthState['authFailure']) => void
  clearAuthFailure: () => void
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set) => ({
        token: null,
        host: null,
        shop: null,
        isLoading: false, // Changed from true to false - we'll set it to true only when actually loading
        isHydrated: false,
        authFailure: null,

        setToken: (token) => {
          set({ token, isLoading: false }, false, 'auth/setToken')
        },

        setHostAndShop: (host, shop) => {
          set({ host, shop }, false, 'auth/setHostAndShop')
        },

        clearToken: () => {
          set({ token: null, host: null, shop: null, isLoading: false }, false, 'auth/clearToken')
        },

        setIsLoading: (value) => {
          set({ isLoading: value }, false, 'auth/setIsLoading')
        },

        setHydrated: () => {
          set({ isHydrated: true }, false, 'auth/setHydrated')
        },

        setAuthFailure: (authFailure) => {
          if (!authFailure) {
            set({ authFailure: null, isLoading: false }, false, 'auth/setAuthFailure')
            return
          }

          set(
            {
              authFailure: {
                ...authFailure,
                reauthStatus: authFailure.reauthStatus ?? null,
              },
              isLoading: false,
            },
            false,
            'auth/setAuthFailure'
          )
        },

        clearAuthFailure: () => {
          set({ authFailure: null }, false, 'auth/clearAuthFailure')
        },
      }),
      {
        name: 'fordeer-auth-storage',
        partialize: (state) => ({
          token: state.token,
          host: state.host,
          shop: state.shop,
        }),
        onRehydrateStorage: () => {
          return (state, error) => {
            if (error) {
              state?.setHydrated()
            } else {
              state?.setHydrated()
              if (state?.token) {
                state.setIsLoading(false)
              }
            }
          }
        },
      }
    ),
    {
      name: 'Auth Store',
      enabled: process.env.NODE_ENV !== 'production',
    }
  )
)
